import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Feather, Image, LayoutGrid, ScrollText, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const FORMATS = [
  ["novel", "Book", BookOpen, "Novel or non-fiction", [["upload", "I have a manuscript to upload"], ["write", "Write it here from scratch"]]],
  ["picture", "Picture book", Image, "Page images with a little text", [["picture", "Add my page images"]]],
  ["comic", "Comic", LayoutGrid, "Panels and speech balloons", [["setup", "Set up style & characters"]]],
  ["manga", "Manga", ScrollText, "Right-to-left comic pages", [["setup", "Set up style & characters"]]],
  ["webtoon", "Webtoon", Smartphone, "Vertical-scroll episodes", [["setup", "Set up style & characters"]]],
  ["poetry", "Poetry", Feather, "A collection of poems", [["poems", "Start writing poems"]]],
];

export function FirstProjectWizard({ open, onClose }) {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [fmt, setFmt] = useState(null);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [busy, setBusy] = useState(false);
  const f = FORMATS.find((x) => x[0] === fmt);
  const create = async (tab) => {
    setBusy(true);
    try {
      const { data } = await api.post("/books", { title: title.trim(), author: author.trim(), format: fmt === "picture" ? "novel" : fmt });
      toast.success("Project created — follow the checklist on My Books any time");
      onClose(); nav(`/app/books/${data.id}?tab=${tab}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const close = () => { setStep(0); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-xl bg-slate-950 border-amber-500/30" data-testid="first-project-wizard">
        <DialogHeader><DialogTitle className="font-display">{["What are you making?", "Name your project", "How do you want to start?"][step]}</DialogTitle></DialogHeader>
        <p className="text-[11px] font-mono text-amber-300">Step {step + 1} of 3</p>
        {step === 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {FORMATS.map(([k, l, Icon, d]) => (
              <button key={k} type="button" onClick={() => { setFmt(k); setStep(1); }} data-testid={`wizard-format-${k}`}
                className={`text-left rounded-lg border p-3 transition-colors ${fmt === k ? "border-amber-500 bg-amber-500/10" : "border-slate-800 hover:border-amber-500/50"}`}>
                <Icon size={18} className="text-amber-300 mb-2" /><p className="text-sm text-slate-100">{l}</p><p className="text-[11px] text-slate-500">{d}</p>
              </button>
            ))}
          </div>
        )}
        {step === 1 && (
          <div className="space-y-3">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Working title" maxLength={200} data-testid="wizard-title" autoFocus />
            <Input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Author name (as it should appear on the cover)" maxLength={200} data-testid="wizard-author" />
            <p className="text-[11px] text-slate-500">You can change both later.</p>
          </div>
        )}
        {step === 2 && f && (
          <div className="space-y-2">
            {f[4].map(([tab, l]) => <Button key={tab} disabled={busy} onClick={() => create(tab)} className="w-full justify-start bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-100" data-testid={`wizard-start-${tab}`}>{busy ? "Creating…" : l}</Button>)}
          </div>
        )}
        <div className="flex justify-between">
          <Button variant="ghost" className="text-slate-400" onClick={step ? () => setStep(step - 1) : close} data-testid="wizard-back">{step ? "Back" : "Cancel"}</Button>
          {step === 1 && <Button className="bg-amber-600 hover:bg-amber-500" disabled={!title.trim()} onClick={() => setStep(2)} data-testid="wizard-next">Next</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
